// ProductLinksService — product sourcing links + cost data.
//
// The Google Sheet (via the OMS backend, GET /api/product-links) is ALWAYS the
// default source. Results are cached to localStorage under `productLinksData`
// in the shape the Receiving/Orders pages read ({ id, link, allLinks, ...cost }).
//
// A manual Excel upload (ExcelUploader) still writes to that same cache so its
// data shows immediately, but it is NOT sticky: the next load/refresh fetches
// the sheet again and replaces it. The sheet always wins — no flag to clear.

import api from './api';

export interface ProductInfo {
  id: string;
  wholesale: number;
  weight: number;
  serviceFee: number;
  deliveryFee: number;
  customsFee: number;
  importTax: number;
  salesTax: number;
  totalCost: number;
  retailPrice: number;
  shopee: number;
  final: number;
  profitMargins: string;
  link: string;
  allLinks?: string[];
}

declare global {
  interface Window {
    productLinksService: any;
  }
}

const STORAGE_KEY = 'productLinksData';
const SOURCE_KEY = 'productLinksSource';

class ProductLinksService {
  private cache: Map<string, ProductInfo> = new Map();
  private lastFetchTime: number = 0;
  private cacheExpirationMs: number = 3600000; // 1 hour
  private inFlight: Promise<ProductInfo[]> | null = null;

  constructor() {
    // Warm the cache from any locally stored data so the UI paints instantly,
    // but treat it as stale: lastFetchTime stays 0 so the first fetch still
    // pulls fresh data from the sheet (otherwise old cached/Excel data would be
    // considered "fresh" for an hour and the sheet would never be read).
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (storedData) {
      try {
        this.populateCache(JSON.parse(storedData));
      } catch (error) {
        console.error('Error parsing stored product links data:', error);
      }
    }
    this.lastFetchTime = 0;
  }

  private populateCache(products: any[]) {
    this.cache.clear();
    products.forEach((product) => {
      if (!product || !product.id) return;
      const full: ProductInfo = {
        id: String(product.id),
        wholesale: product.wholesale || 0,
        weight: product.weight || 0,
        serviceFee: product.serviceFee || 0,
        deliveryFee: product.deliveryFee || 0,
        customsFee: product.customsFee || 0,
        importTax: product.importTax || 0,
        salesTax: product.salesTax || 0,
        totalCost: product.totalCost || 0,
        retailPrice: product.retailPrice || 0,
        shopee: product.shopee || 0,
        final: product.final || 0,
        profitMargins: product.profitMargins || '',
        link: product.link || '',
        allLinks: product.allLinks || (product.link ? [product.link] : []),
      };
      this.cache.set(full.id, full);
    });
    this.lastFetchTime = Date.now();
  }

  /**
   * Fetch product links. The Google Sheet (via backend) is always the source;
   * cached data is only used within the freshness window or as a fallback when
   * the sheet fetch fails.
   */
  async fetchProductLinks(force = false): Promise<ProductInfo[]> {
    const fresh = Date.now() - this.lastFetchTime < this.cacheExpirationMs;
    if (!force && fresh && this.cache.size > 0) {
      return Array.from(this.cache.values());
    }
    if (this.inFlight) return this.inFlight;

    this.inFlight = (async () => {
      try {
        const response = await api.get('/api/product-links');
        const products: ProductInfo[] = Array.isArray(response.data) ? response.data : [];
        // Populate the in-memory cache FIRST so links work this session even if
        // persistence below fails — the full sheet payload (~1.6MB) can blow the
        // localStorage quota, and that must not wipe the freshly fetched data.
        this.populateCache(products);
        // Persist only the fields the Receiving/Orders pages read from storage.
        // Storing the full rows (12 cost fields + links each) overflows the
        // ~5MB localStorage quota; the slim shape is ~5x smaller and fits.
        try {
          const slim = products.map((p) => ({ id: p.id, link: p.link, allLinks: p.allLinks }));
          // Clear the old value first so its space is freed before writing —
          // guarantees room even if a prior (bloated) payload is still stored.
          localStorage.removeItem(STORAGE_KEY);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
          localStorage.setItem(SOURCE_KEY, 'sheets');
        } catch (persistErr: any) {
          console.warn('Product links not persisted (localStorage quota):', persistErr?.name || persistErr);
        }
        return Array.from(this.cache.values());
      } catch (error) {
        console.error('Failed to fetch product links from Google Sheets:', error);
        // Fall back to whatever is cached so the UI degrades gracefully.
        return Array.from(this.cache.values());
      } finally {
        this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  async getProductLink(id: string): Promise<string | null> {
    if (this.cache.size === 0 || Date.now() - this.lastFetchTime > this.cacheExpirationMs) {
      await this.fetchProductLinks();
    }
    return this.cache.get(String(id))?.link || null;
  }

  async getProductInfo(id: string): Promise<ProductInfo | null> {
    if (this.cache.size === 0 || Date.now() - this.lastFetchTime > this.cacheExpirationMs) {
      await this.fetchProductLinks();
    }
    return this.cache.get(String(id)) || null;
  }

  clearCache() {
    this.cache.clear();
    this.lastFetchTime = 0;
  }

  /** Switch the active source back to Google Sheets and force a refresh. */
  async useGoogleSheets(): Promise<ProductInfo[]> {
    localStorage.setItem(SOURCE_KEY, 'sheets');
    this.clearCache();
    return this.fetchProductLinks(true);
  }

  /**
   * Force the backend to re-sync the Google Sheet into the product_links DB
   * table now (instead of waiting for the 30-min cron), then refresh locally.
   * Returns the sync summary { synced, withLink }.
   */
  async syncNow(): Promise<{ synced?: number; withLink?: number }> {
    const response = await api.post('/api/product-links/sync');
    // Pull the freshly synced rows into the local cache.
    await this.fetchProductLinks(true);
    return response.data || {};
  }

  /**
   * Test connectivity to the configured Google Sheet via the backend.
   * When spreadsheetId is omitted the backend tests the saved config.
   */
  async testConnection(
    spreadsheetId?: string,
    sheetName?: string
  ): Promise<{ success: boolean; rowCount?: number; error?: string }> {
    try {
      const response = await api.post('/api/settings/google-sheets/test', {
        spreadsheetId,
        sheetName,
      });
      return { success: true, rowCount: response.data?.rowCount };
    } catch (error: any) {
      const serverError = error?.response?.data?.error;
      return {
        success: false,
        error: serverError || (error instanceof Error ? error.message : 'Unknown error'),
      };
    }
  }
}

// Singleton
const productLinksService = new ProductLinksService();

// Expose for debugging / cross-component cache invalidation
window.productLinksService = productLinksService;

export default productLinksService;
